import type { z } from 'zod';
import {
  Appointment,
  Blackout,
  ClientDetail,
  Deleted,
  HoursWritten,
  Preference,
  Service,
} from '../wire';
import type { IntentKind, OutboxEntry } from './entry';

/**
 * Every kind of write the PWA can make, as data: the request it becomes and the
 * schema its answer is parsed with.
 *
 * A table rather than a `switch` in the drain loop, for the reason `core-api`'s
 * route registry gives for the same move: adding a write means adding a row, and
 * a row with no request shape does not compile. The drain has no idea what an
 * appointment is — it sends intents and settles them — which is why it can be
 * tested exhaustively against §8.2 without a single view being involved.
 *
 * Note what is *not* here: nothing computes a price, a duration or an end time.
 * `CreateAppointmentBody` has no field for any of them and the server looks them
 * up from `service_id` (obligation B7). An offline device holding stale prices
 * that it then asserts on reconnect is exactly the failure that rule exists to
 * prevent, and the shape of the request makes it unrepresentable here.
 */

export interface IntentRequest {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: Record<string, unknown> | undefined;
  schema: z.ZodType<unknown>;
}

export function describeIntent(entry: OutboxEntry): IntentRequest {
  const t = entry.target;
  const body = { ...entry.payload, client_intent_id: entry.id };

  switch (entry.kind) {
    case 'appointment.create':
      return { method: 'POST', path: '/appointments', body, schema: Appointment };
    case 'appointment.reschedule':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/reschedule`,
        body,
        schema: Appointment,
      };
    case 'appointment.cancel':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/cancel`,
        body,
        schema: Appointment,
      };
    case 'appointment.status':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/status`,
        body,
        schema: Appointment,
      };
    case 'client.create':
      return { method: 'POST', path: '/clients', body, schema: ClientDetail };
    case 'client.update':
      return {
        method: 'PATCH',
        path: `/clients/${required(t, 'client_id')}`,
        body,
        schema: ClientDetail,
      };
    case 'client.archive':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/archive`,
        body,
        schema: ClientDetail,
      };
    case 'client.identity.add':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/identities`,
        body,
        schema: ClientDetail,
      };
    case 'client.identity.remove':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/identities/remove`,
        body,
        schema: ClientDetail,
      };
    case 'preference.create':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/preferences`,
        body,
        schema: Preference,
      };
    case 'preference.update':
      return {
        method: 'PATCH',
        path: `/clients/${required(t, 'client_id')}/preferences/${required(t, 'preference_id')}`,
        body,
        schema: Preference,
      };
    case 'preference.delete':
      // A `DELETE` carries no body, so `client_intent_id` has nowhere to ride.
      // The `Idempotency-Key` header is the only form of intent here — which is
      // precisely why the outbox always sends the header as well as the field.
      return {
        method: 'DELETE',
        path: `/clients/${required(t, 'client_id')}/preferences/${required(t, 'preference_id')}`,
        schema: Deleted,
      };
    case 'service.create':
      return { method: 'POST', path: '/services', body, schema: Service };
    case 'service.update':
      return {
        method: 'PATCH',
        path: `/services/${required(t, 'service_id')}`,
        body,
        schema: Service,
      };
    case 'hours.set':
      return { method: 'PUT', path: '/settings/hours', body, schema: HoursWritten };
    case 'blackout.create':
      return { method: 'POST', path: '/blackouts', body, schema: Blackout };
    case 'blackout.delete':
      return {
        method: 'DELETE',
        path: `/blackouts/${required(t, 'blackout_id')}`,
        schema: Deleted,
      };
  }
}

/**
 * What the settled response means for the mirror.
 *
 * Kept beside the request shapes rather than in the mirror, because the pairing
 * "this intent produced that kind of row" is one fact and belongs in one file.
 */
export type SettlementKind =
  'appointment' | 'client' | 'preference' | 'service' | 'hours' | 'blackout' | 'none';

export function settlementKind(kind: IntentKind): SettlementKind {
  if (kind.startsWith('appointment.')) return 'appointment';
  if (kind.startsWith('client.')) return 'client';
  if (kind.startsWith('preference.')) return 'preference';
  if (kind.startsWith('service.')) return 'service';
  if (kind === 'hours.set') return 'hours';
  if (kind.startsWith('blackout.')) return 'blackout';
  return 'none';
}

function required(target: Record<string, string>, key: string): string {
  const value = target[key];
  if (value === undefined) {
    throw new Error(`intent is missing target.${key}`);
  }
  return value;
}
